import { useEffect } from 'react';
import { Box } from '@nimbus-ds/components';
import { Country } from '@tiendanube/common/src/enums';
import { useNotifications } from 'App/capacitor';
import FeatureFlag from 'App/components/FeatureFlag';
import OnboardingCompletedAlert from 'App/components/OnboardingCompletedAlert';
import ShowByCountry from 'App/components/ShowByCountry';
import ShowByRole from 'App/components/ShowByRole/ShowByRole';
import { ONBOARDING_PROMOTE, PROJECT_SHIVA_TAG } from 'App/featuresFlags';
import {
  HeaderContent,
  IonPageStratus,
} from 'commons/components/IonPageStratus';
import { useRoles } from 'domains/Auth/authSlice/useRoles';
import { useGetTags } from 'domains/Auth/hooks';
import { useTransactionFeeToPay } from 'domains/Billing/TransactionFees/pages/TransactionFeesPage/hooks';
import { useTranslationDashboard } from 'domains/Dashboard/hooks';
import { useTotalOrders } from 'domains/Orders/Orders/hooks';
import { useHasAccessToStats } from 'domains/Statistics';
import {
  ActivatePushNotificationsAlert,
  AdditionalSettings,
  AlertNotifications,
  AlertPromote,
  DashboardHelpLink,
  DashboardModals,
  ExpiresInChip,
  FreeContents,
  ImproveYourStore,
  NewAlertNotifications,
  NewEcommerceNaPraticaCard,
  NewOnboardingTasks,
  PendingOrders,
  RecommendationsList,
  StoreStats,
  UpdateNotificationsAlert,
  GiftCard,
  HeaderPage,
  PromoteList,
  OnboardingPromoteTasks,
} from './components';
import useOnboardingTasks from './components/OnboardingTasks/useOnboardingTasks';

// This component duplicates DashboardPage to implement New Onboarding feature [NA-5773]. Should be unified after testing results.
function NewDashboardPage() {
  const t = useTranslationDashboard();
  const { isLoadingHasOrders, hasOrder, totalAllOrders } = useTotalOrders();
  const { validateRoles } = useRoles();
  const hasAccessToStats = useHasAccessToStats();
  const requireTransactionFeesFetch = validateRoles([
    'full',
    'nuvem_pago',
    'nuvem_envio',
  ]);
  useTransactionFeeToPay(requireTransactionFeesFetch);
  const {
    getOnboarding,
    hasCompleted,
    hasCompletedOrSkipped,
    isPos,
    isOnlineStore,
    onboardingTasks,
    isLoading: isLoadingOnboardingTasks,
  } = useOnboardingTasks();
  useNotifications();
  const hasOrderRoleNonFull = validateRoles('orders', 'full');
  const hasOrderRoleStatsNonFull = validateRoles('statistics', 'full');
  const hasRoleFull = validateRoles('full');
  const tags = useGetTags();
  const hasOnboardingPromoteTag = tags.includes(ONBOARDING_PROMOTE);
  const hasOnboardingIncomplete = hasOnboardingPromoteTag
    ? !hasCompletedOrSkipped
    : !hasCompleted && totalAllOrders < 3;
  const hasOnboardingComplete = hasOnboardingPromoteTag
    ? hasCompleted
    : hasCompleted && totalAllOrders < 3;
  const showEcommerceNaPractica = isOnlineStore && hasOnboardingComplete;

  const pageWidth =
    !hasOnboardingIncomplete && !hasOnboardingPromoteTag ? 'medium' : 'small';
  useEffect(() => {
    // If hasOnboardingPromoteTag we need make this request always
    if (hasOnboardingPromoteTag || hasOnboardingIncomplete) getOnboarding();
  }, [getOnboarding, hasOnboardingIncomplete, hasOnboardingPromoteTag]);

  return (
    <IonPageStratus
      width={pageWidth}
      headerContent={<HeaderContent title={t('title')} />}
      headerTop={<ActivatePushNotificationsAlert />}
    >
      <UpdateNotificationsAlert />
      <Box
        display="flex"
        flexDirection="column"
        gap={hasOnboardingPromoteTag ? '6' : '10'}
      >
        <Box
          display="flex"
          flexDirection="column"
          gap={hasOnboardingPromoteTag ? '4' : '8'}
        >
          <ExpiresInChip />
          {hasOnboardingComplete && <OnboardingCompletedAlert />}
          {hasOnboardingIncomplete ? (
            <>
              <NewAlertNotifications />
              <FeatureFlag flag={PROJECT_SHIVA_TAG}>
                <GiftCard />
              </FeatureFlag>
            </>
          ) : (
            <AlertNotifications />
          )}

          {/* Display Pending Orders block if the user has the "full" role and has completed the onboarding or has sales */}
          {/* Display Pending Orders block always if the user has only the "orders" role */}
          {((hasRoleFull && (hasCompleted || hasOrder)) ||
            hasOrderRoleNonFull) && (
            <ShowByRole includeRoles="orders">
              <PendingOrders />
            </ShowByRole>
          )}

          {/* Display the Statistics block if the user has "orders" and "statistics" roles or "full" role, and has orders */}
          {(hasOrderRoleNonFull || hasRoleFull) &&
            hasAccessToStats &&
            !isLoadingHasOrders && (
              <ShowByRole includeRoles="statistics">
                <StoreStats showDailyStats={hasOrder} />
              </ShowByRole>
            )}

          {/* Display the Statistics block if the user has only the "statistics" role */}
          {!hasOrderRoleNonFull &&
            hasOrderRoleStatsNonFull &&
            hasAccessToStats && (
              <ShowByRole includeRoles="statistics">
                <StoreStats />
              </ShowByRole>
            )}

          {hasOnboardingIncomplete && (
            <Box display="flex" flexDirection="column" gap="6">
              <HeaderPage />
              <NewOnboardingTasks />
            </Box>
          )}
        </Box>

        <FeatureFlag
          flag={ONBOARDING_PROMOTE}
          renderElse={
            <>
              {showEcommerceNaPractica && (
                <ShowByRole includeRoles="full">
                  <NewEcommerceNaPraticaCard />
                </ShowByRole>
              )}
              {isOnlineStore && !hasOnboardingIncomplete && (
                <ShowByRole includeRoles="full">
                  <AdditionalSettings />
                </ShowByRole>
              )}
              {!isPos && hasOnboardingComplete && (
                <ShowByCountry excludeCountries={[Country.MX]}>
                  <FreeContents />
                </ShowByCountry>
              )}
              {!hasOnboardingIncomplete && <ImproveYourStore />}
            </>
          }
        >
          <>
            <PromoteList tasks={onboardingTasks?.promoteTasks ?? []} />
            {isLoadingOnboardingTasks && !onboardingTasks?.promoteTasks && (
              <OnboardingPromoteTasks.Skeleton type="promote" itemsCount={3} />
            )}
            {showEcommerceNaPractica && (
              <ShowByRole includeRoles="full">
                <NewEcommerceNaPraticaCard />
              </ShowByRole>
            )}
            <RecommendationsList
              tasks={onboardingTasks?.recommendationTasks ?? []}
            />
            {!hasOnboardingIncomplete && <AlertPromote />}
          </>
        </FeatureFlag>
      </Box>
      <DashboardModals />
      <DashboardHelpLink
        isPos={isPos}
        hasOnboardingIncomplete={hasOnboardingIncomplete}
      />
    </IonPageStratus>
  );
}

export default NewDashboardPage;
